// Config for the table-plus-modals tabs on Employee Details (see EmployeeRecordTab).
// Field types: "text", "textarea", "date". Column types: "text", "date", "clamp".

export const JOB_DESCRIPTION_TAB = {
  title: "Job Description",
  singular: "Job Description",
  viewTitle: (employeeName) => `${employeeName} Job Description Details`,
  columns: [
    { key: "dateOfJd", label: "Date of JD", type: "date" },
    { key: "description", label: "Job Description", type: "clamp" },
  ],
  fields: [
    { key: "dateOfJd", label: "Date of JD", type: "date", required: true },
    { key: "description", label: "Job Description", type: "textarea", required: true, maxLength: 4000 },
  ],
  sample: [
    { dateOfJd: "2023-02-09", description: "Maintain and support the company's internal systems and respond to user requests." },
    { dateOfJd: "2024-01-15", description: "Lead the helpdesk team and report weekly service metrics to the department manager." },
  ],
};

export const EMPLOYMENT_HISTORY_TAB = {
  title: "Employment History",
  singular: "Employment History",
  viewTitle: () => "Employment History Details",
  columns: [
    { key: "employerName", label: "Employer Name", type: "text" },
    { key: "designation", label: "Designation", type: "text" },
    { key: "department", label: "Department", type: "text" },
    { key: "startDate", label: "Start Date", type: "date" },
    { key: "endDate", label: "End Date", type: "date" },
    { key: "reasonForLeaving", label: "Reason for Leaving", type: "text" },
  ],
  fields: [
    { key: "employerName", label: "Employer Name", type: "text", required: true, maxLength: 100 },
    { key: "designation", label: "Designation", type: "text", required: true, maxLength: 100 },
    { key: "department", label: "Department", type: "text", required: true, maxLength: 100 },
    { key: "startDate", label: "Start Date", type: "date", required: true },
    { key: "endDate", label: "End Date", type: "date", required: true },
    { key: "address", label: "Address", type: "text", maxLength: 200 },
    { key: "reasonForLeaving", label: "Reason for Leaving", type: "text", required: true, maxLength: 200 },
  ],
  validate: (values) =>
    values.startDate && values.endDate && values.endDate < values.startDate
      ? "End date cannot be earlier than start date."
      : "",
  sample: [
    { employerName: "Acme Technologies", designation: "Support Analyst", department: "IT", startDate: "2019-03-01", endDate: "2021-06-30", address: "12 Marina, Lagos", reasonForLeaving: "Career growth" },
    { employerName: "Globex Limited", designation: "Systems Administrator", department: "Operations", startDate: "2021-08-01", endDate: "2023-01-31", address: "5 Wuse II, Abuja", reasonForLeaving: "Relocation" },
  ],
};

export const INSURANCE_TAB = {
  title: "Insurance",
  singular: "Insurance",
  viewTitle: (employeeName) => `${employeeName} Insurance Details`,
  columns: [
    { key: "insuranceFor", label: "Insurance For", type: "text" },
    { key: "insuranceType", label: "Insurance Type", type: "text" },
    { key: "provider", label: "Provider", type: "text" },
    { key: "startDate", label: "Start Date", type: "date" },
    { key: "endDate", label: "End Date", type: "date" },
    { key: "remark", label: "Remark", type: "text" },
  ],
  fields: [
    { key: "insuranceFor", label: "Insurance For", type: "select", required: true, options: ["Employee", "Spouse", "Child", "Family"] },
    { key: "insuranceType", label: "Insurance Type", type: "select", required: true, options: ["Health", "Life", "Accident", "Pension"] },
    { key: "provider", label: "Provider", type: "select", required: true, options: ["AXA Mansard", "Leadway Assurance", "AIICO Insurance", "Hygeia HMO", "Other"] },
    { key: "policyNumber", label: "Policy Number", type: "text", required: true, maxLength: 60 },
    { key: "coverageAmount", label: "Coverage Amount", type: "text", maxLength: 40 },
    { key: "startDate", label: "Start Date", type: "date", required: true },
    { key: "endDate", label: "End Date", type: "date", required: true },
    { key: "remark", label: "Remark", type: "text", maxLength: 200 },
  ],
  validate: (values) =>
    values.startDate && values.endDate && values.endDate < values.startDate
      ? "End date cannot be earlier than start date."
      : "",
  sample: [
    { insuranceFor: "Employee", insuranceType: "Health", provider: "Hygeia HMO", policyNumber: "HG-100234", coverageAmount: "5,000,000", startDate: "2023-03-01", endDate: "2024-02-29", remark: "Annual plan" },
    { insuranceFor: "Employee", insuranceType: "Life", provider: "AXA Mansard", policyNumber: "AX-558120", coverageAmount: "20,000,000", startDate: "2023-03-01", endDate: "2028-02-29", remark: "-" },
  ],
};

export const RESOURCE_TAB = {
  title: "Company Resource Information",
  singular: "Resource",
  modalNoun: "Provided Resources",
  viewTitle: (employeeName) => `${employeeName} Company Resource Details`,
  columns: [
    { key: "resourceType", label: "Resource Type", type: "text" },
    { key: "modelSerial", label: "Model/Serial No.", type: "text" },
    { key: "providedDate", label: "Provided Date", type: "date" },
    { key: "description", label: "Resource Description", type: "text" },
    { key: "status", label: "Status", type: "status" },
  ],
  // Field order follows the view modal in the design.
  fields: [
    { key: "resourceType", label: "Resource Type", type: "select", required: true, options: ["Laptop", "Phone", "ID Card", "Access Card", "Vehicle", "Other"] },
    { key: "providedDate", label: "Provided Date", type: "date", required: true },
    { key: "modelSerial", label: "Model/Serial No.", type: "text", required: true, maxLength: 80 },
    { key: "description", label: "Resource Description", type: "text", required: true, maxLength: 200 },
    { key: "status", label: "Status", type: "select", required: true, options: ["Active", "Returned", "Lost", "Damaged"] },
  ],
  sample: [
    { resourceType: "Laptop", modelSerial: "Dell Latitude 5420 / SN48213", description: "Company laptop with charger", providedDate: "2023-02-09", status: "Active" },
    { resourceType: "Access Card", modelSerial: "AC-20931", description: "Office access card", providedDate: "2023-02-09", status: "Active" },
    { resourceType: "Phone", modelSerial: "Samsung Galaxy A54 / IMEI 3569021847", description: "Company mobile phone", providedDate: "2023-02-15", status: "Active" },
    { resourceType: "ID Card", modelSerial: "ID-77413", description: "Staff identification card", providedDate: "2023-02-09", status: "Active" },
    { resourceType: "Laptop", modelSerial: "HP EliteBook 840 / SN90217", description: "Replacement laptop after hardware fault", providedDate: "2023-11-20", status: "Returned" },
    { resourceType: "Vehicle", modelSerial: "Toyota Hilux / LND-482-KJ", description: "Pool vehicle for field visits", providedDate: "2024-03-04", status: "Active" },
    { resourceType: "Phone", modelSerial: "iPhone 11 / IMEI 3527114820", description: "Old company phone", providedDate: "2023-02-15", status: "Lost" },
    { resourceType: "Other", modelSerial: "HS-3300", description: "Wireless headset", providedDate: "2024-06-12", status: "Damaged" },
  ],
};

export const TRAINING_TAB = {
  title: "Training Information",
  singular: "Training",
  viewTitle: (employeeName) => `${employeeName} Training Details`,
  columns: [
    { key: "title", label: "Title", type: "text" },
    { key: "provider", label: "Provider", type: "text" },
    { key: "startDate", label: "Start Date", type: "date" },
    { key: "endDate", label: "End Date", type: "date" },
    { key: "grade", label: "Certificate/Grade", type: "text" },
    { key: "remark", label: "Remark", type: "text" },
  ],
  fields: [
    { key: "title", label: "Title", type: "text", required: true, maxLength: 100 },
    { key: "provider", label: "Provider", type: "text", required: true, maxLength: 100 },
    { key: "startDate", label: "Start Date", type: "date", required: true },
    { key: "endDate", label: "End Date", type: "date", required: true },
    { key: "grade", label: "Certificate/Grade", type: "text", required: true, maxLength: 100 },
    { key: "remark", label: "Remark", type: "text", required: true, maxLength: 200 },
  ],
  validate: (values) =>
    values.startDate && values.endDate && values.endDate < values.startDate
      ? "End date cannot be earlier than start date."
      : "",
  sample: [
    { title: "ITIL Foundation", provider: "Axelos", startDate: "2023-05-01", endDate: "2023-05-05", grade: "Certified", remark: "Passed first attempt" },
    { title: "Customer Service Excellence", provider: "Worldbay Academy", startDate: "2023-09-11", endDate: "2023-09-12", grade: "Merit", remark: "In house" },
  ],
};

const SUPERVISORS = {
  "Michael Brown": "Firstnamelastname@worldbaytech.com",
  "Alice Johnson": "Alice.johnson@worldbaytech.com",
  "James Carter": "James.carter@worldbaytech.com",
  "Sophia Martin": "Sophia.martin@worldbaytech.com",
};

export const SUPERVISOR_TAB = {
  title: "Supervisor Information",
  singular: "Supervisor",
  viewTitle: (employeeName) => `${employeeName} Supervisor Details`,
  columns: [
    { key: "supervisor", label: "Supervisor Name", type: "person", subKey: "supervisorEmail" },
    { key: "level", label: "Supervisor Level", type: "text" },
    { key: "status", label: "Status", type: "status" },
  ],
  fields: [
    { key: "level", label: "Supervisor Level", type: "select", required: true, options: ["Level 1", "Level 2", "Level 3"] },
    { key: "supervisor", label: "Supervisor", type: "select", required: true, options: Object.keys(SUPERVISORS) },
  ],
  // The view modal lists the supervisor before the level, unlike the form.
  viewFields: [
    { key: "supervisor", label: "Supervisor", type: "text" },
    { key: "level", label: "Supervisor Level", type: "text" },
  ],
  // The form only captures the name and level; email and status are derived.
  prepare: (values) => ({ supervisorEmail: SUPERVISORS[values.supervisor] ?? "-", status: "Active" }),
  sample: [
    { supervisor: "Michael Brown", supervisorEmail: SUPERVISORS["Michael Brown"], level: "Level 1", status: "Active" },
    { supervisor: "Alice Johnson", supervisorEmail: SUPERVISORS["Alice Johnson"], level: "Level 2", status: "Active" },
  ],
};

export const LEAVE_TAB = {
  title: "Leave Information (View Only, Application will come from a separate form)",
  singular: "Leave",
  readOnly: true,
  viewTitle: (employeeName) => `${employeeName} Leave Details`,
  columns: [
    { key: "leaveType", label: "Leave Type", type: "text" },
    { key: "startDate", label: "Start Date", type: "date" },
    { key: "endDate", label: "End Date", type: "date" },
    { key: "totalDays", label: "Total Days", type: "text" },
    { key: "remark", label: "Remark", type: "text" },
    { key: "status", label: "Status", type: "status" },
  ],
  fields: [
    { key: "leaveType", label: "Leave Type", type: "text" },
    { key: "startDate", label: "Start Date", type: "date" },
    { key: "endDate", label: "End Date", type: "date" },
    { key: "totalDays", label: "Total Days", type: "text" },
    { key: "remark", label: "Remark", type: "text" },
    { key: "status", label: "Status", type: "text" },
  ],
  sample: [
    { leaveType: "Annual", startDate: "2026-04-06", endDate: "2026-04-10", totalDays: "5", remark: "Family trip", status: "Approved" },
    { leaveType: "Sick", startDate: "2026-06-15", endDate: "2026-06-16", totalDays: "2", remark: "Medical appointment", status: "Approved" },
    { leaveType: "Annual", startDate: "2026-12-21", endDate: "2026-12-31", totalDays: "9", remark: "Year end break", status: "Pending" },
    { leaveType: "Casual", startDate: "2026-02-03", endDate: "2026-02-03", totalDays: "1", remark: "Personal errand", status: "Rejected" },
  ],
};

export const KPI_TAB = {
  title: "Performance & KPI",
  singular: "Performance & KPI",
  viewTitle: () => "Performance & KPI Details",
  columns: [
    { key: "reviewPeriod", label: "Review Period", type: "text" },
    { key: "objective", label: "KPI / Objective", type: "text" },
    { key: "target", label: "Target", type: "text" },
    { key: "achievement", label: "Achievement", type: "text" },
    { key: "rating", label: "Rating", type: "text" },
    { key: "comments", label: "Reviewer Comments", type: "text" },
  ],
  fields: [
    { key: "reviewPeriod", label: "Review Period", type: "text", required: true, maxLength: 60 },
    { key: "objective", label: "KPI / Objective", type: "text", required: true, maxLength: 200 },
    { key: "target", label: "Target", type: "text", required: true, maxLength: 100 },
    { key: "achievement", label: "Achievement", type: "text", required: true, maxLength: 100 },
    { key: "rating", label: "Rating", type: "select", required: true, options: ["1 - Poor", "2 - Fair", "3 - Good", "4 - Very Good", "5 - Excellent"] },
    { key: "comments", label: "Reviewer Comments", type: "text", maxLength: 300 },
  ],
  sample: [
    { reviewPeriod: "Q3 2025", objective: "Deliver payroll module", target: "Q3 release", achievement: "Released Q3", rating: "4 - Very Good", comments: "Strong ownership" },
    { reviewPeriod: "Q4 2025", objective: "Reduce ticket backlog", target: "Under 20 open tickets", achievement: "14 open tickets", rating: "5 - Excellent", comments: "Exceeded target" },
    { reviewPeriod: "Q1 2026", objective: "Roll out onboarding checklist", target: "All new hires", achievement: "80% of new hires", rating: "3 - Good", comments: "Needs follow up on contractors" },
  ],
};

export const KPI_ITEMS_TAB = {
  title: "Performance & KPI",
  singular: "KPI",
  viewTitle: () => "KPI Details",
  columns: [
    { key: "kpiTitle", label: "KPI Title", type: "text" },
    { key: "kpiDescription", label: "KPI Description", type: "text" },
    { key: "kpiIndicator", label: "KPI Indicator", type: "text" },
    { key: "status", label: "Status", type: "status" },
  ],
  fields: [
    { key: "kpiTitle", label: "KPI Title", type: "text", required: true, maxLength: 100 },
    { key: "kpiDescription", label: "KPI Description", type: "text", required: true, maxLength: 300 },
    { key: "kpiIndicator", label: "KPI Indicator", type: "text", required: true, maxLength: 200 },
    { key: "status", label: "Status", type: "select", required: true, options: ["Pending", "Approved"] },
  ],
  sample: [
    { kpiTitle: "Payroll module delivery", kpiDescription: "Ship the payroll module to production", kpiIndicator: "Release date met", status: "Pending" },
    { kpiTitle: "Ticket resolution time", kpiDescription: "Resolve support tickets within the agreed SLA", kpiIndicator: "Average hours to resolve", status: "Approved" },
    { kpiTitle: "Onboarding completion", kpiDescription: "Complete onboarding checklist for new hires", kpiIndicator: "Percent of hires onboarded", status: "Approved" },
  ],
};
